import type { ToolCall, ToolCallExecution, Usage } from "@webuild-ai/admin/models/components";
import type { Citation } from "@/types/citations";
import type { MessageType } from "@/types/sse";

/**
 * Helpers shared by the two consumers of the workflow SSE stream, `ChatStore`
 * and the Workflow Playground's `useWorkflowExecution`. Each used to carry its
 * own copy, which is how the two filters could drift apart unnoticed.
 */

/** Backend progress chatter that should never be shown to users. */
const SYSTEM_MESSAGE_PREFIX = /^(System:|Debug:|Status:)/i;

/** Phrases the agent runtime emits while it is routing a reply internally. */
const SYSTEM_MESSAGE_PHRASES = [
  "Direct call completed",
  "response via agent reply mechanism",
  "agent reply mechanism",
] as const;

/**
 * Whether streamed text is backend chatter (a routing phrase or a
 * `System:`/`Debug:`/`Status:` prefix) that must be dropped. Callers decide
 * what an empty string means; this only classifies non-empty text.
 */
export function isSystemChatter(data: string): boolean {
  return (
    SYSTEM_MESSAGE_PHRASES.some((phrase) => data.includes(phrase)) ||
    Boolean(data.match(SYSTEM_MESSAGE_PREFIX))
  );
}

/** Whether a parsed JSON value is a non-null object, which is all that can carry fields. */
export const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

/** The value if it is text, otherwise `undefined`. */
export const readString = (value: unknown): string | undefined =>
  typeof value === "string" ? value : undefined;

/** The value if it is a number, otherwise `undefined`. */
export const readNumber = (value: unknown): number | undefined =>
  typeof value === "number" ? value : undefined;

const MESSAGE_TYPES: readonly MessageType[] = ["user", "assistant", "system"];

/** Whether a value is one of the message types the UI renders. */
export const isMessageType = (value: unknown): value is MessageType =>
  MESSAGE_TYPES.some((type) => type === value);

/**
 * Whether a value is a list of objects. The entries of a citation list are
 * only checked for being objects, since the backend adds fields over time and
 * the store reads them defensively.
 */
export const isCitationList = (value: unknown): value is Citation[] =>
  Array.isArray(value) && value.every(isRecord);

/** Whether a value is a list of tool calls (objects, checked as for citations). */
const isToolCallList = (value: unknown): value is ToolCall[] =>
  Array.isArray(value) && value.every(isRecord);

/** The list as tool executions, or `undefined` when the value is not such a list. */
export const readToolCallExecutions = (value: unknown): ToolCallExecution[] | undefined =>
  isToolCallList(value) ? value : undefined;

/** The list as tool calls, or `undefined` when the value is not such a list. */
export const readToolCalls = (value: unknown): ToolCall[] | undefined =>
  isToolCallList(value) ? value : undefined;

/** A token counter, or 0 when the value is not a number. */
const readCounter = (value: unknown): number => readNumber(value) ?? 0;

/**
 * Map a payload's usage to the camelCase shape, reading either the snake_case
 * or the camelCase counters, or `undefined` when the payload carries no usage
 * object. A counter that is absent, zero or not a number reads as 0.
 */
export function readUsage(payload: unknown): Usage | undefined {
  const usage = isRecord(payload) ? payload.usage : undefined;

  if (!isRecord(usage)) return undefined;

  return {
    promptTokens: readCounter(usage.prompt_tokens) || readCounter(usage.promptTokens),
    completionTokens: readCounter(usage.completion_tokens) || readCounter(usage.completionTokens),
    totalTokens: readCounter(usage.total_tokens) || readCounter(usage.totalTokens),
  };
}

/** As {@link readUsage}, but a payload with no usage reports zero tokens. */
export function readUsageOrZero(payload: unknown): Usage {
  return readUsage(payload) ?? { promptTokens: 0, completionTokens: 0, totalTokens: 0 };
}
